'use client'

import { useState } from 'react'
import Image from 'next/image'
import { safeImageSrc } from '@/lib/imageHelpers'
import { supabaseImageLoader } from '@/lib/supabaseImageLoader'

interface SafeImageProps {
  src?: string | null
  alt: string
  width?: number
  height?: number
  fill?: boolean
  className?: string
  priority?: boolean
  sizes?: string
}

/**
 * Safe Image component with validation and fallback for Supabase images.
 * Uses unoptimized images and custom loader to bypass Next.js Image Optimizer.
 */
export default function SafeImage({
  src,
  alt,
  width,
  height,
  fill = false,
  className = '',
  priority = false,
  sizes,
}: SafeImageProps) {
  const [showFallback, setShowFallback] = useState(false)
  
  const finalSrc = safeImageSrc(src)
  
  // If no valid source or error occurred, show gradient placeholder
  if (!finalSrc || showFallback) {
    return (
      <div 
        className={`flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 ${className}`}
        style={fill ? undefined : { width, height }}
      >
        <div className="text-center text-white">
          <svg 
            className="w-8 h-8 mx-auto mb-1 opacity-75" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H9m3 0V9" 
            />
          </svg>
          <p className="text-xs opacity-75">Bild</p>
        </div>
      </div>
    )
  }
  
  if (fill) {
    return (
      <Image
        src={finalSrc}
        alt={alt}
        fill
        className={className}
        priority={priority}
        sizes={sizes}
        loader={supabaseImageLoader}
        unoptimized
        onError={() => setShowFallback(true)}
      />
    )
  }
  
  return (
    <Image
      src={finalSrc}
      alt={alt}
      width={width || 100}
      height={height || 100}
      className={className}
      priority={priority}
      loader={supabaseImageLoader}
      unoptimized
      onError={() => setShowFallback(true)}
    />
  )
}